import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  FaEdit,
  FaExternalLinkAlt,
  FaFilePdf,
  FaImage,
  FaPlus,
  FaSyncAlt,
  FaTrash,
  FaVideo,
} from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import UserTable from "../components/UserTable";
import { authHeaders } from "../utils/authHeaders";
import "./UserManagement.css";

const CALON_STATUS_OPTIONS = [
  { value: "", label: "Semua Status" },
  { value: "approved", label: "Approved" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Reject" },
];

const EMPTY_SUCCESS_STORY_FORM = {
  name: "",
  address: "",
  business_type: "",
  short_story: "",
  video_url: "",
};

const EMPTY_NEWS_FORM = {
  title: "",
  hashtags: "",
  description: "",
  thumbnail_url: "",
  pdf_url: "",
  photo_url: "",
  video_url: "",
};

export default function UserManagement() {
  const { user } = useContext(AuthContext);
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const canViewSahabatKakao = isAdmin || role === "field team";
  const canViewCalonSahabat = isAdmin || role === "field team" || role === "management";
  const envApi = process.env.REACT_APP_API_URL || "/api";
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const isLocalWeb =
    typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
  const apiUrl = (() => {
    const raw = String(envApi || "").trim();
    if (!/^https?:\/\//i.test(raw)) return raw || "/api";
    try {
      const u = new URL(raw);
      const isLocalApi = /^(localhost|127\.0\.0\.1)$/i.test(u.hostname);
      if (!isLocalWeb && isLocalApi) return `${currentOrigin}/api`;
      if (typeof window !== "undefined" && window.location.protocol === "https:" && u.protocol === "http:") {
        return raw.replace(/^http:/i, "https:");
      }
      return raw;
    } catch {
      return raw || "/api";
    }
  })();
  const apiOrigin = apiUrl.startsWith("http")
    ? apiUrl.replace(/\/api\/?$/, "")
    : currentOrigin || "";
  const uploadBase = apiOrigin || "/api";

  const [activeTab, setActiveTab] = useState("users");
  const [sahabatKakao, setSahabatKakao] = useState([]);
  const [loadingSahabatKakao, setLoadingSahabatKakao] = useState(false);
  const [sahabatKakaoError, setSahabatKakaoError] = useState("");
  const [sahabatKakaoSearch, setSahabatKakaoSearch] = useState("");
  const [sahabatKakaoInstitution, setSahabatKakaoInstitution] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const [calonSahabat, setCalonSahabat] = useState([]);
  const [loadingCalon, setLoadingCalon] = useState(false);
  const [calonError, setCalonError] = useState("");
  const [updatingCalonId, setUpdatingCalonId] = useState(null);
  const [selectedCalon, setSelectedCalon] = useState(null);
  const [calonStatusFilter, setCalonStatusFilter] = useState("");
  const [successStoryForm, setSuccessStoryForm] = useState(EMPTY_SUCCESS_STORY_FORM);
  const [successStories, setSuccessStories] = useState([]);
  const [loadingSuccessStories, setLoadingSuccessStories] = useState(false);
  const [savingSuccessStory, setSavingSuccessStory] = useState(false);
  const [editingSuccessStoryId, setEditingSuccessStoryId] = useState(null);
  const [successStoryModalOpen, setSuccessStoryModalOpen] = useState(false);
  const [news, setNews] = useState([]);
  const [newsForm, setNewsForm] = useState(EMPTY_NEWS_FORM);
  const [loadingNews, setLoadingNews] = useState(false);
  const [savingNews, setSavingNews] = useState(false);
  const [editingNewsId, setEditingNewsId] = useState(null);
  const [newsModalOpen, setNewsModalOpen] = useState(false);

  const resolveMediaUrl = (url) => {
    const raw = String(url || "").trim();
    if (!raw) return "";
    if (raw.startsWith("blob:")) return "";
    if (raw.startsWith("data:")) return raw;
    if (/^https?:\/\//i.test(raw)) {
      try {
        const u = new URL(raw);
        const isLocalHostUrl = /^(localhost|127\.0\.0\.1)$/i.test(u.hostname);
        if (isLocalHostUrl && u.pathname.startsWith("/uploads/")) return `${currentOrigin}${u.pathname}`;
        if (u.pathname.startsWith("/uploads/")) return `${uploadBase}${u.pathname}`;
      } catch {
        return raw;
      }
      return raw;
    }
    if (raw.startsWith("/uploads/")) return `${uploadBase}${raw}`;
    if (raw.startsWith("/")) return apiOrigin ? `${apiOrigin}${raw}` : raw;
    return apiOrigin ? `${apiOrigin}/${raw}` : raw;
  };

  const resolveThumbnailUrl = (url) => {
    const raw = String(url || "").trim();
    const fileMatch = raw.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
    if (fileMatch?.[1]) return `${apiUrl}/news/thumbnail/${encodeURIComponent(fileMatch[1])}`;
    try {
      const parsed = new URL(raw);
      if (parsed.hostname === "drive.google.com") {
        const id = parsed.searchParams.get("id");
        if (id) return `${apiUrl}/news/thumbnail/${encodeURIComponent(id)}`;
      }
    } catch {
      return resolveMediaUrl(raw);
    }
    return resolveMediaUrl(raw);
  };

  const fetchTestimonials = async () => {
    if (!isAdmin) return;
    setLoadingTestimonials(true);
    try {
      const res = await axios.get(`${apiUrl}/testimonials/admin`, {
        headers: authHeaders(user),
      });
      setTestimonials(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch {
      setTestimonials([]);
    } finally {
      setLoadingTestimonials(false);
    }
  };

  const fetchSahabatKakao = async () => {
    if (!canViewSahabatKakao) return;
    setLoadingSahabatKakao(true);
    setSahabatKakaoError("");
    try {
      const res = await axios.get(`${apiUrl}/user/sahabat-kakao`, {
        headers: authHeaders(user),
      });
      setSahabatKakao(Array.isArray(res?.data?.data) ? res.data.data : []);
      setSahabatKakaoInstitution(String(res?.data?.institution || ""));
    } catch (err) {
      setSahabatKakao([]);
      setSahabatKakaoError(err?.response?.data?.message || "Gagal memuat data Sahabat Kakao.");
    } finally {
      setLoadingSahabatKakao(false);
    }
  };

  const fetchCalonSahabat = async () => {
    if (!canViewCalonSahabat) return;
    setLoadingCalon(true);
    setCalonError("");
    try {
      const res = await axios.get(`${apiUrl}/sahabat-registrations/admin`, {
        headers: authHeaders(user),
      });
      setCalonSahabat(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (err) {
      setCalonSahabat([]);
      setCalonError(err?.response?.data?.message || "Gagal memuat data calon sahabat kakao.");
    } finally {
      setLoadingCalon(false);
    }
  };

  const fetchSuccessStories = async () => {
    if (!isAdmin) return;
    setLoadingSuccessStories(true);
    try {
      const res = await axios.get(`${apiUrl}/success-stories/admin`, {
        headers: authHeaders(user),
      });
      setSuccessStories(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch {
      setSuccessStories([]);
    } finally {
      setLoadingSuccessStories(false);
    }
  };

  const fetchNews = async () => {
    if (!isAdmin) return;
    setLoadingNews(true);
    try {
      const res = await axios.get(`${apiUrl}/news`, {
        headers: authHeaders(user),
      });
      setNews(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (err) {
      setNews([]);
      alert(err?.response?.data?.message || "Gagal memuat berita.");
    } finally {
      setLoadingNews(false);
    }
  };

  useEffect(() => {
    if (activeTab === "sahabat-kakao") fetchSahabatKakao();
    if (activeTab === "testimonials") fetchTestimonials();
    if (activeTab === "calon-sahabat") fetchCalonSahabat();
    if (activeTab === "kisah-sukses") fetchSuccessStories();
    if (activeTab === "news") fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (isAdmin) return;
    const allowedTabs = [
      ...(canViewSahabatKakao ? ["sahabat-kakao"] : []),
      ...(canViewCalonSahabat ? ["calon-sahabat"] : []),
    ];
    if (!allowedTabs.includes(activeTab)) {
      setActiveTab(canViewSahabatKakao ? "sahabat-kakao" : "calon-sahabat");
    }
  }, [activeTab, canViewCalonSahabat, canViewSahabatKakao, isAdmin]);

  const filteredSahabatKakao = useMemo(() => {
    const query = sahabatKakaoSearch.trim().toLowerCase();
    if (!query) return sahabatKakao;
    return sahabatKakao.filter((item) =>
      [
        item.full_name,
        item.phone,
        item.email,
        item.institution,
        item.desa,
        item.kecamatan,
        item.kabupaten,
        item.provinsi,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [sahabatKakao, sahabatKakaoSearch]);

  const updateStatus = async (id, status) => {
    if (!id || updatingId) return;
    setUpdatingId(id);
    try {
      const res = await axios.patch(
        `${apiUrl}/testimonials/${id}/status`,
        { status },
        { headers: authHeaders(user) }
      );
      if (res?.data?.success) {
        setTestimonials((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
      } else {
        alert(res?.data?.message || "Gagal memperbarui status.");
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal memperbarui status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteTestimonial = async (id) => {
    if (!id || updatingId) return;
    const ok = window.confirm("Hapus testimoni ini secara permanen?");
    if (!ok) return;

    setUpdatingId(id);
    try {
      const res = await axios.delete(`${apiUrl}/testimonials/${id}`, {
        headers: authHeaders(user),
      });
      if (res?.data?.success) {
        setTestimonials((prev) => prev.filter((t) => t.id !== id));
      } else {
        alert(res?.data?.message || "Gagal menghapus testimoni.");
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal menghapus testimoni.");
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value).replace("T", " ").slice(0, 19);
    return dt.toLocaleString("id-ID", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatWholeNumber = (value) => {
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return "-";
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    }).format(Math.round(n));
  };

  const updateCalonStatus = async (id, status) => {
    if (!id || updatingCalonId) return;
    setUpdatingCalonId(id);
    try {
      const res = await axios.patch(
        `${apiUrl}/sahabat-registrations/admin/${id}/status`,
        { status },
        { headers: authHeaders(user) }
      );

      if (res?.data?.success) {
        setCalonSahabat((prev) => prev.map((item) => (item.id === id ? { ...item, status } : item)));
        setSelectedCalon((prev) => (prev?.id === id ? { ...prev, status } : prev));
      } else {
        alert(res?.data?.message || "Gagal memperbarui status calon sahabat kakao.");
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal memperbarui status calon sahabat kakao.");
    } finally {
      setUpdatingCalonId(null);
    }
  };

  const exportCalonToExcel = () => {
    const rows = filteredCalonSahabat.map((item) => ({
      ID: item.id,
      Nama: item.full_name || "",
      "No HP": item.phone || "",
      Email: item.email || "",
      Institusi: item.institusi || "",
      Alamat: item.alamat || "",
      Desa: item.desa || "",
      Kecamatan: item.kecamatan || "",
      Kabupaten: item.kabupaten || "",
      Provinsi: item.provinsi || "",
      "Badan Hukum": item.legal_entity || "",
      Jenis: item.legal_entity_type || "",
      "Luas (ha)": item.land_area_ha ?? "",
      "Kapasitas Bibit/Tahun": item.nursery_capacity_per_year
        ? formatWholeNumber(item.nursery_capacity_per_year)
        : "",
      Pengalaman: item.business_experience || "",
      Catatan: item.additional_notes || "",
      Status: item.status || "pending",
      "Tanggal Daftar": formatDateTime(item.created_at),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CalonSahabat");
    XLSX.writeFile(workbook, "calon_sahabat_kakao.xlsx");
  };

  const filteredCalonSahabat = calonSahabat.filter((item) => {
    if (!calonStatusFilter) return true;
    return String(item.status || "pending").toLowerCase() === calonStatusFilter;
  });

  const handleSuccessStoryInput = (e) => {
    const { name, value } = e.target;
    setSuccessStoryForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitSuccessStory = async (e) => {
    e.preventDefault();

    const requiredFields = [
      ["name", "Nama"],
      ["address", "Alamat"],
      ["business_type", "Jenis Usaha"],
      ["short_story", "Kisah Singkat"],
      ["video_url", "Link video"],
    ];
    const missing = requiredFields.find(([key]) => !String(successStoryForm[key] || "").trim());
    if (missing) {
      alert(`${missing[1]} wajib diisi.`);
      return;
    }

    setSavingSuccessStory(true);
    try {
      const request = editingSuccessStoryId
        ? axios.put(`${apiUrl}/success-stories/${editingSuccessStoryId}`, successStoryForm, {
            headers: authHeaders(user),
          })
        : axios.post(`${apiUrl}/success-stories`, successStoryForm, {
            headers: authHeaders(user),
          });
      const res = await request;
      if (res?.data?.success) {
        alert(res.data.message || "Kisah sukses berhasil disimpan.");
        setSuccessStoryForm(EMPTY_SUCCESS_STORY_FORM);
        setEditingSuccessStoryId(null);
        setSuccessStoryModalOpen(false);
        fetchSuccessStories();
      } else {
        alert(res?.data?.message || "Gagal menyimpan kisah sukses.");
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal menyimpan kisah sukses.");
    } finally {
      setSavingSuccessStory(false);
    }
  };

  const editSuccessStory = (story) => {
    setEditingSuccessStoryId(story.id);
    setSuccessStoryForm({
      name: story.name || "",
      address: story.address || "",
      business_type: story.business_type || "",
      short_story: story.short_story || "",
      video_url: story.video_url || "",
    });
    setSuccessStoryModalOpen(true);
  };

  const resetSuccessStoryForm = () => {
    setEditingSuccessStoryId(null);
    setSuccessStoryForm(EMPTY_SUCCESS_STORY_FORM);
  };

  const openAddSuccessStory = () => {
    resetSuccessStoryForm();
    setSuccessStoryModalOpen(true);
  };

  const closeSuccessStoryModal = () => {
    if (savingSuccessStory) return;
    setSuccessStoryModalOpen(false);
    resetSuccessStoryForm();
  };

  const handleNewsInput = (e) => {
    const { name, value } = e.target;
    setNewsForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetNewsForm = () => {
    setEditingNewsId(null);
    setNewsForm(EMPTY_NEWS_FORM);
  };

  const openAddNews = () => {
    resetNewsForm();
    setNewsModalOpen(true);
  };

  const editNews = (item) => {
    setEditingNewsId(item.id);
    setNewsForm({
      title: item.title || "",
      hashtags: item.hashtags || "",
      description: item.description || "",
      thumbnail_url: item.thumbnail_url || "",
      pdf_url: item.pdf_url || "",
      photo_url: item.photo_url || "",
      video_url: item.video_url || "",
    });
    setNewsModalOpen(true);
  };

  const closeNewsModal = () => {
    if (savingNews) return;
    setNewsModalOpen(false);
    resetNewsForm();
  };

  const submitNews = async (e) => {
    e.preventDefault();

    const missingField = [
      ["title", "Judul"],
      ["hashtags", "Hashtag"],
      ["description", "Deskripsi"],
    ].find(([key]) => !String(newsForm[key] || "").trim());

    if (missingField) {
      alert(`${missingField[1]} wajib diisi.`);
      return;
    }

    setSavingNews(true);
    try {
      const request = editingNewsId
        ? axios.put(`${apiUrl}/news/${editingNewsId}`, newsForm, {
            headers: authHeaders(user),
          })
        : axios.post(`${apiUrl}/news`, newsForm, {
            headers: authHeaders(user),
          });
      const res = await request;
      if (!res?.data?.success) throw new Error(res?.data?.message || "Gagal menyimpan berita.");

      alert(res.data.message || "Berita berhasil disimpan.");
      setNewsModalOpen(false);
      resetNewsForm();
      await fetchNews();
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Gagal menyimpan berita.");
    } finally {
      setSavingNews(false);
    }
  };

  const deleteNews = async (item) => {
    const confirmed = window.confirm(`Hapus berita "${item.title}" secara permanen?`);
    if (!confirmed) return;

    try {
      const res = await axios.delete(`${apiUrl}/news/${item.id}`, {
        headers: authHeaders(user),
      });
      if (!res?.data?.success) throw new Error(res?.data?.message || "Gagal menghapus berita.");
      setNews((prev) => prev.filter((entry) => entry.id !== item.id));
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Gagal menghapus berita.");
    }
  };

  return (
    <div className="user-management-wrapper">
      <div className="user-management-tabs">
        {isAdmin && (
          <button
            className={`tab-button ${activeTab === "users" ? "active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            Users List
          </button>
        )}
        {isAdmin && (
          <button
            className={`tab-button ${activeTab === "testimonials" ? "active" : ""}`}
            onClick={() => setActiveTab("testimonials")}
          >
            Testimonials
          </button>
        )}
        {canViewSahabatKakao && (
          <button
            className={`tab-button ${activeTab === "sahabat-kakao" ? "active" : ""}`}
            onClick={() => setActiveTab("sahabat-kakao")}
          >
            Sahabat Kakao
          </button>
        )}
        <button
          className={`tab-button ${activeTab === "calon-sahabat" ? "active" : ""}`}
          onClick={() => setActiveTab("calon-sahabat")}
        >
          Calon Sahabat Kakao
        </button>
        {isAdmin && (
          <button
            className={`tab-button ${activeTab === "kisah-sukses" ? "active" : ""}`}
            onClick={() => setActiveTab("kisah-sukses")}
          >
            Kisah Sukses
          </button>
        )}
        {isAdmin && (
          <button
            className={`tab-button ${activeTab === "news" ? "active" : ""}`}
            onClick={() => setActiveTab("news")}
          >
            News
          </button>
        )}
      </div>

      <div className="user-management-content">
        {activeTab === "users" && isAdmin && (
          <div className="table-responsive">
            <UserTable />
          </div>
        )}

        {activeTab === "testimonials" && (
          <div className="tm-admin-wrap">
            {!isAdmin ? (
              <div className="module-placeholder">Akses hanya untuk admin.</div>
            ) : loadingTestimonials ? (
              <div className="module-placeholder">Memuat testimoni...</div>
            ) : testimonials.length === 0 ? (
              <div className="module-placeholder">Belum ada data testimoni.</div>
            ) : (
              <div className="tm-admin-list">
                {testimonials.map((t) => (
                  <div className="tm-admin-card" key={t.id}>
                    <img src={resolveMediaUrl(t.photo_url)} alt={t.name} />
                    <div className="tm-admin-body">
                      <div className="tm-admin-top">
                        <h4>{t.name}</h4>
                        <span className={`tm-status tm-${t.status}`}>{t.status}</span>
                      </div>
                      <small>{t.origin || "-"}</small>
                      <p>{t.message}</p>
                      <div className="tm-admin-actions">
                        <button className="tm-btn-approve" onClick={() => updateStatus(t.id, "approved")} disabled={updatingId === t.id}>Tampilkan</button>
                        <button className="tm-btn-pending" onClick={() => updateStatus(t.id, "pending")} disabled={updatingId === t.id}>Pending</button>
                        <button className="tm-btn-reject" onClick={() => updateStatus(t.id, "rejected")} disabled={updatingId === t.id}>Tolak</button>
                        <button className="tm-btn-delete" onClick={() => deleteTestimonial(t.id)} disabled={updatingId === t.id}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "sahabat-kakao" && (
          <div className="table-responsive sahabat-scope-wrap">
            {!canViewSahabatKakao ? (
              <div className="module-placeholder">Akses tidak diizinkan.</div>
            ) : loadingSahabatKakao ? (
              <div className="module-placeholder">Memuat data Sahabat Kakao...</div>
            ) : sahabatKakaoError ? (
              <div className="module-placeholder">{sahabatKakaoError}</div>
            ) : (
              <div className="sahabat-scope-panel">
                <div className="sahabat-scope-toolbar">
                  <div>
                    <strong>{filteredSahabatKakao.length}</strong> dari {sahabatKakao.length} Sahabat Kakao
                    <span className="sahabat-scope-institution">
                      {isAdmin ? "Semua institusi" : `Institusi: ${sahabatKakaoInstitution || "sesuai akun"}`}
                    </span>
                  </div>
                  <div className="sahabat-scope-actions">
                    <input
                      type="search"
                      value={sahabatKakaoSearch}
                      onChange={(event) => setSahabatKakaoSearch(event.target.value)}
                      placeholder="Cari nama, kontak, atau wilayah"
                      aria-label="Cari Sahabat Kakao"
                    />
                    <button type="button" className="um-export-btn" onClick={fetchSahabatKakao}>
                      <FaSyncAlt /> Refresh
                    </button>
                  </div>
                </div>

                <div className="table-scroll-wrapper sahabat-scope-table-scroll">
                  <table className="user-table sahabat-scope-table">
                    <thead>
                      <tr>
                        <th>Nama</th>
                        <th>Institusi</th>
                        <th>No HP</th>
                        <th>Email</th>
                        <th>Jenis Kelamin</th>
                        <th>Desa</th>
                        <th>Kecamatan</th>
                        <th>Kabupaten</th>
                        <th>Provinsi</th>
                        <th>Jumlah Kebun</th>
                        <th>Luas Kebun (ha)</th>
                        <th>Layanan</th>
                        <th>Pesanan</th>
                        <th>Selesai</th>
                        <th>Terdaftar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSahabatKakao.map((item) => (
                        <tr key={item.id}>
                          <td><strong>{item.full_name || "-"}</strong></td>
                          <td>{item.institution || "-"}</td>
                          <td>{item.phone || "-"}</td>
                          <td>{item.email || "-"}</td>
                          <td>{item.gender || "-"}</td>
                          <td>{item.desa || "-"}</td>
                          <td>{item.kecamatan || "-"}</td>
                          <td>{item.kabupaten || "-"}</td>
                          <td>{item.provinsi || "-"}</td>
                          <td>{formatWholeNumber(item.jumlah_kebun || 0)}</td>
                          <td>{item.luas_kebun ?? "-"}</td>
                          <td>{formatWholeNumber(item.service_count || 0)}</td>
                          <td>{formatWholeNumber(item.order_count || 0)}</td>
                          <td>{formatWholeNumber(item.completed_order_count || 0)}</td>
                          <td>{formatDateTime(item.created_at)}</td>
                        </tr>
                      ))}
                      {filteredSahabatKakao.length === 0 && (
                        <tr>
                          <td colSpan="15" className="calon-empty-row">
                            Belum ada Sahabat Kakao yang sesuai.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "calon-sahabat" && (
          <div className="table-responsive calon-wrap">
            {!canViewCalonSahabat ? (
              <div className="module-placeholder">Akses tidak diizinkan.</div>
            ) : loadingCalon ? (
              <div className="module-placeholder">Memuat data calon sahabat kakao...</div>
            ) : calonError ? (
              <div className="module-placeholder">{calonError}</div>
            ) : calonSahabat.length === 0 ? (
              <div className="module-placeholder">Belum ada data pendaftar.</div>
            ) : (
              <div className="calon-panel">
                <div className="calon-toolbar">
                  <div className="calon-toolbar-meta">
                    <strong>{filteredCalonSahabat.length}</strong> dari {calonSahabat.length} data calon sahabat kakao
                    {role === "field team" && (
                      <span> - Institusi: {user?.institution || "sesuai akun"}</span>
                    )}
                  </div>
                  <div className="calon-toolbar-actions">
                    <label className="calon-filter">
                      <span>Filter Aksi</span>
                      <select value={calonStatusFilter} onChange={(e) => setCalonStatusFilter(e.target.value)}>
                        {CALON_STATUS_OPTIONS.map((option) => (
                          <option key={option.value || "all"} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button className="um-export-btn" onClick={exportCalonToExcel}>
                      Export to Excel
                    </button>
                  </div>
                </div>

                <div className="table-scroll-wrapper calon-table-scroll">
                  <table className="user-table calon-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nama</th>
                        <th>No HP</th>
                        <th>Email</th>
                        <th>Institusi</th>
                        <th>Alamat</th>
                        <th>Desa</th>
                        <th>Kecamatan</th>
                        <th>Kabupaten</th>
                        <th>Provinsi</th>
                        <th>Badan Hukum</th>
                        <th>Jenis</th>
                        <th>Luas (ha)</th>
                        <th>Kapasitas Bibit/Tahun</th>
                        <th>Pengalaman</th>
                        <th>Catatan</th>
                        <th>Status</th>
                        <th>Tanggal Daftar</th>
                        {isAdmin && <th>Aksi</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCalonSahabat.map((r) => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td>{r.full_name || "-"}</td>
                          <td>{r.phone || "-"}</td>
                          <td>{r.email || "-"}</td>
                          <td>{r.institusi || "-"}</td>
                          <td>{r.alamat || "-"}</td>
                          <td>{r.desa || "-"}</td>
                          <td>{r.kecamatan || "-"}</td>
                          <td>{r.kabupaten || "-"}</td>
                          <td>{r.provinsi || "-"}</td>
                          <td>{r.legal_entity || "-"}</td>
                          <td>{r.legal_entity_type || "-"}</td>
                          <td>{r.land_area_ha ?? "-"}</td>
                          <td>{r.nursery_capacity_per_year ? formatWholeNumber(r.nursery_capacity_per_year) : "-"}</td>
                          <td>{r.business_experience || "-"}</td>
                          <td>{r.additional_notes || "-"}</td>
                          <td>
                            <span className={`calon-status calon-status-${r.status || "pending"}`}>
                              {r.status || "pending"}
                            </span>
                          </td>
                          <td>{formatDateTime(r.created_at)}</td>
                          {isAdmin && (
                            <td>
                              <div className="calon-actions">
                                <button className="calon-btn calon-btn-view" onClick={() => setSelectedCalon(r)}>
                                  View
                                </button>
                                <button
                                  className="calon-btn calon-btn-approved"
                                  onClick={() => updateCalonStatus(r.id, "approved")}
                                  disabled={updatingCalonId === r.id}
                                >
                                  Approved
                                </button>
                                <button
                                  className="calon-btn calon-btn-pending"
                                  onClick={() => updateCalonStatus(r.id, "pending")}
                                  disabled={updatingCalonId === r.id}
                                >
                                  Pending
                                </button>
                                <button
                                  className="calon-btn calon-btn-reject"
                                  onClick={() => updateCalonStatus(r.id, "rejected")}
                                  disabled={updatingCalonId === r.id}
                                >
                                  Reject
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                      {filteredCalonSahabat.length === 0 && (
                        <tr>
                          <td colSpan={isAdmin ? "19" : "18"} className="calon-empty-row">
                            Tidak ada data untuk filter status ini.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "kisah-sukses" && (
          <div className="success-story-admin-wrap">
            {!isAdmin ? (
              <div className="module-placeholder">Akses hanya untuk admin.</div>
            ) : (
              <>
                <div className="success-story-list-panel">
                  <div className="success-story-list-head">
                    <h2>Daftar Kisah Sukses</h2>
                    <div className="success-story-list-actions">
                      <button type="button" className="success-story-secondary" onClick={fetchSuccessStories}>
                        Refresh
                      </button>
                      <button type="button" className="success-story-submit" onClick={openAddSuccessStory}>
                        Tambah Kisah Sukses
                      </button>
                    </div>
                  </div>

                  {loadingSuccessStories ? (
                    <div className="module-placeholder">Memuat kisah sukses...</div>
                  ) : successStories.length === 0 ? (
                    <div className="module-placeholder">Belum ada kisah sukses.</div>
                  ) : (
                    <div className="success-story-list">
                      {successStories.map((story) => (
                        <article className="success-story-item" key={story.id}>
                          <div className="success-story-item-main">
                            <h3>{story.name}</h3>
                            <p className="success-story-item-meta">
                              {story.business_type || "-"} · {story.address || "-"}
                            </p>
                            <p>{story.short_story || "-"}</p>
                            <a href={story.video_url} target="_blank" rel="noopener noreferrer">
                              {story.video_url}
                            </a>
                          </div>
                          <button
                            type="button"
                            className="success-story-edit-btn"
                            onClick={() => editSuccessStory(story)}
                          >
                            Edit
                          </button>
                        </article>
                      ))}
                    </div>
                  )}
                </div>

                {successStoryModalOpen && (
                  <div className="success-story-modal-backdrop" onClick={closeSuccessStoryModal}>
                    <div className="success-story-modal" onClick={(e) => e.stopPropagation()}>
                      <form className="success-story-form" onSubmit={submitSuccessStory}>
                        <div className="success-story-modal-head">
                          <div className="success-story-form-head">
                            <h2>{editingSuccessStoryId ? "Edit Kisah Sukses" : "Tambah Kisah Sukses"}</h2>
                            <p>Isi data narasumber, ringkasan kisah, dan tautan video yang akan ditampilkan ke halaman publik.</p>
                          </div>
                          <button
                            type="button"
                            className="success-story-modal-close"
                            onClick={closeSuccessStoryModal}
                            disabled={savingSuccessStory}
                          >
                            x
                          </button>
                        </div>

                        <div className="success-story-grid">
                          <label className="success-story-field">
                            <span>Nama</span>
                            <input
                              name="name"
                              value={successStoryForm.name}
                              onChange={handleSuccessStoryInput}
                              placeholder="Nama narasumber"
                            />
                          </label>

                          <label className="success-story-field">
                            <span>Alamat</span>
                            <input
                              name="address"
                              value={successStoryForm.address}
                              onChange={handleSuccessStoryInput}
                              placeholder="Desa/Kecamatan/Kabupaten"
                            />
                          </label>

                          <label className="success-story-field">
                            <span>Jenis Usaha</span>
                            <input
                              name="business_type"
                              value={successStoryForm.business_type}
                              onChange={handleSuccessStoryInput}
                              placeholder="Contoh: Pembibitan kakao"
                            />
                          </label>

                          <label className="success-story-field">
                            <span>Link video</span>
                            <input
                              name="video_url"
                              value={successStoryForm.video_url}
                              onChange={handleSuccessStoryInput}
                              placeholder="https://... atau /videos/nama-file.mp4"
                            />
                          </label>

                          <label className="success-story-field full">
                            <span>Kisah Singkat</span>
                            <textarea
                              name="short_story"
                              value={successStoryForm.short_story}
                              onChange={handleSuccessStoryInput}
                              placeholder="Tuliskan ringkasan kisah sukses"
                            />
                          </label>
                        </div>

                        <div className="success-story-actions">
                          <button
                            type="button"
                            className="success-story-secondary"
                            onClick={closeSuccessStoryModal}
                            disabled={savingSuccessStory}
                          >
                            Batal
                          </button>
                          <button type="submit" className="success-story-submit" disabled={savingSuccessStory}>
                            {savingSuccessStory
                              ? "Menyimpan..."
                              : editingSuccessStoryId
                                ? "Update Kisah Sukses"
                                : "Simpan Kisah Sukses"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "news" && (
          <div className="news-admin-wrap">
            {!isAdmin ? (
              <div className="module-placeholder">Akses hanya untuk admin.</div>
            ) : (
              <section className="news-panel">
                <header className="news-panel-head">
                  <div>
                    <h2>Daftar Berita</h2>
                    <p>Kelola berita dan tautan media dari Google Drive atau sumber publik lainnya.</p>
                  </div>
                  <div className="news-head-actions">
                    <button type="button" className="news-icon-button" onClick={fetchNews} title="Muat ulang berita">
                      <FaSyncAlt aria-hidden="true" />
                      <span>Refresh</span>
                    </button>
                    <button type="button" className="news-primary-button" onClick={openAddNews}>
                      <FaPlus aria-hidden="true" />
                      <span>Tambah Berita</span>
                    </button>
                  </div>
                </header>

                {loadingNews ? (
                  <div className="module-placeholder">Memuat berita...</div>
                ) : news.length === 0 ? (
                  <div className="news-empty">
                    <strong>Belum ada berita</strong>
                    <span>Tambahkan berita pertama melalui tombol Tambah Berita.</span>
                  </div>
                ) : (
                  <div className="news-list">
                    {news.map((item) => (
                      <article className="news-item" key={item.id}>
                        <div className={`news-item-main ${item.thumbnail_url ? "has-thumbnail" : ""}`}>
                          {item.thumbnail_url && (
                            <img
                              className="news-item-thumbnail"
                              src={resolveThumbnailUrl(item.thumbnail_url)}
                              alt={`Thumbnail ${item.title}`}
                            />
                          )}
                          <div className="news-item-content">
                            <div className="news-item-title-row">
                              <h3>{item.title}</h3>
                              <span>{formatDateTime(item.updated_at || item.created_at)}</span>
                            </div>
                            <div className="news-hashtags">{item.hashtags}</div>
                            <p>{item.description}</p>
                            <div className="news-meta">
                              <span>Oleh {item.author_name || "Admin"}</span>
                              <div className="news-media-links">
                                {item.pdf_url && (
                                  <a href={item.pdf_url} target="_blank" rel="noopener noreferrer">
                                    <FaFilePdf aria-hidden="true" /> PDF <FaExternalLinkAlt aria-hidden="true" />
                                  </a>
                                )}
                                {item.photo_url && (
                                  <a href={item.photo_url} target="_blank" rel="noopener noreferrer">
                                    <FaImage aria-hidden="true" /> Foto <FaExternalLinkAlt aria-hidden="true" />
                                  </a>
                                )}
                                {item.video_url && (
                                  <a href={item.video_url} target="_blank" rel="noopener noreferrer">
                                    <FaVideo aria-hidden="true" /> Video <FaExternalLinkAlt aria-hidden="true" />
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="news-item-actions">
                          <button type="button" onClick={() => editNews(item)} title="Edit berita">
                            <FaEdit aria-hidden="true" />
                          </button>
                          <button type="button" className="danger" onClick={() => deleteNews(item)} title="Hapus berita">
                            <FaTrash aria-hidden="true" />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )}

            {newsModalOpen && isAdmin && (
              <div className="news-modal-backdrop" onClick={closeNewsModal}>
                <div className="news-modal" onClick={(e) => e.stopPropagation()}>
                  <form onSubmit={submitNews}>
                    <div className="news-modal-head">
                      <div>
                        <h2>{editingNewsId ? "Edit Berita" : "Tambah Berita"}</h2>
                        <p>Gunakan link yang dapat diakses oleh pembaca tanpa meminta izin tambahan.</p>
                      </div>
                      <button type="button" className="news-modal-close" onClick={closeNewsModal} disabled={savingNews} aria-label="Tutup">
                        x
                      </button>
                    </div>

                    <div className="news-modal-body">
                      <div className="news-form-grid">
                        <label className="news-field full">
                          <span>Judul <b>*</b></span>
                          <input name="title" value={newsForm.title} onChange={handleNewsInput} placeholder="Judul berita" maxLength="220" />
                        </label>
                        <label className="news-field full">
                          <span>Hashtag <b>*</b></span>
                          <input name="hashtags" value={newsForm.hashtags} onChange={handleNewsInput} placeholder="#Kakao #Petani #SahabatKakao" maxLength="500" />
                        </label>
                      <label className="news-field full">
                        <span>Deskripsi <b>*</b></span>
                        <textarea name="description" value={newsForm.description} onChange={handleNewsInput} placeholder="Tuliskan isi atau ringkasan berita" maxLength="10000" />
                      </label>
                      <label className="news-field full">
                        <span>Foto Thumbnail (link)</span>
                        <input
                          type="url"
                          name="thumbnail_url"
                          value={newsForm.thumbnail_url}
                          onChange={handleNewsInput}
                          placeholder="https://drive.google.com/... atau https://..."
                        />
                        {newsForm.thumbnail_url && (
                          <div className="news-thumbnail-preview">
                            <img src={resolveThumbnailUrl(newsForm.thumbnail_url)} alt="Preview thumbnail berita" />
                          </div>
                        )}
                      </label>
                      <label className="news-field">
                          <span>Media PDF (link)</span>
                          <input type="url" name="pdf_url" value={newsForm.pdf_url} onChange={handleNewsInput} placeholder="https://drive.google.com/..." />
                        </label>
                        <label className="news-field">
                          <span>Media Foto (link)</span>
                          <input type="url" name="photo_url" value={newsForm.photo_url} onChange={handleNewsInput} placeholder="https://..." />
                        </label>
                        <label className="news-field full">
                          <span>Media Video (link)</span>
                          <input type="url" name="video_url" value={newsForm.video_url} onChange={handleNewsInput} placeholder="https://youtube.com/... atau https://drive.google.com/..." />
                        </label>
                      </div>
                    </div>

                    <div className="news-form-actions">
                      <button type="button" className="news-cancel-button" onClick={closeNewsModal} disabled={savingNews}>Batal</button>
                      <button type="submit" className="news-primary-button" disabled={savingNews}>
                        {savingNews ? "Menyimpan..." : editingNewsId ? "Simpan Perubahan" : "Tambah Berita"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedCalon && (
        <div className="calon-modal-backdrop" onClick={() => setSelectedCalon(null)}>
          <div className="calon-modal" onClick={(e) => e.stopPropagation()}>
            <div className="calon-modal-head">
              <div>
                <h3>{selectedCalon.full_name || "Detail Calon Sahabat Kakao"}</h3>
                <span className={`calon-status calon-status-${selectedCalon.status || "pending"}`}>
                  {selectedCalon.status || "pending"}
                </span>
              </div>
              <button className="calon-modal-close" onClick={() => setSelectedCalon(null)}>
                x
              </button>
            </div>

            <div className="calon-modal-body">
              {[
                ["No HP", selectedCalon.phone],
                ["Email", selectedCalon.email],
                ["Institusi", selectedCalon.institusi],
                ["Alamat", selectedCalon.alamat],
                ["Desa", selectedCalon.desa],
                ["Kecamatan", selectedCalon.kecamatan],
                ["Kabupaten", selectedCalon.kabupaten],
                ["Provinsi", selectedCalon.provinsi],
                ["Badan Hukum", selectedCalon.legal_entity],
                ["Jenis", selectedCalon.legal_entity_type],
                ["Luas (ha)", selectedCalon.land_area_ha],
                [
                  "Kapasitas Bibit/Tahun",
                  selectedCalon.nursery_capacity_per_year
                    ? formatWholeNumber(selectedCalon.nursery_capacity_per_year)
                    : "-",
                ],
                ["Pengalaman", selectedCalon.business_experience],
                ["Catatan", selectedCalon.additional_notes],
                ["Tanggal Daftar", formatDateTime(selectedCalon.created_at)],
              ].map(([label, value]) => (
                <div className="calon-detail-row" key={label}>
                  <div className="calon-detail-label">{label}</div>
                  <div className="calon-detail-value">{value || "-"}</div>
                </div>
              ))}
            </div>

            <div className="calon-modal-foot">
              <button className="calon-btn calon-btn-pending" onClick={() => updateCalonStatus(selectedCalon.id, "pending")} disabled={updatingCalonId === selectedCalon.id}>
                Pending
              </button>
              <button className="calon-btn calon-btn-approved" onClick={() => updateCalonStatus(selectedCalon.id, "approved")} disabled={updatingCalonId === selectedCalon.id}>
                Approved
              </button>
              <button className="calon-btn calon-btn-reject" onClick={() => updateCalonStatus(selectedCalon.id, "rejected")} disabled={updatingCalonId === selectedCalon.id}>
                Reject
              </button>
              <button className="calon-btn calon-btn-view" onClick={() => setSelectedCalon(null)}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


